<div class="space-y-6">
  <div class="flex items-start justify-between backdrop-blur-sm">
    <div>
      <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Chatbot Widget Configuration</h2>
      <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Integrate the chatbot into your website by customizing
        the source and user identification.</p>
    </div>
  </div>
  <!-- HTML code Snippet -->
  <div
    class="relative overflow-visible backdrop-blur-sm bg-white dark:bg-gray-900 rounded-2xl border border-gray-200/90 dark:border-gray-700/70 bg-gradient-to-br from-white via-gray-50/80 to-violet-50/30 dark:from-gray-900/80 dark:via-gray-900/50 dark:to-violet-950/20 shadow-sm shadow-gray-900/5 dark:shadow-black/20 p-6 sm:p-8">
    <div
      class="pointer-events-none absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-violet-500 to-indigo-600 rounded-l-2xl">
    </div>

    <div
      class="flex gap-10 w-full mt-2 border border-gray-200/50 dark:border-gray-700/30 shadow-sm dark:shadow-gray-400/20 p-3 rounded-lg">
      <!-- Snippet Section -->
      <div class="w-full">
        <div class="flex items-center gap-2.5 mb-4">
          <div
            class="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500/10 to-indigo-600/5 ring-1 ring-violet-500/15 dark:from-violet-400/5 dark:to-indigo-500/5 dark:ring-violet-400/10">
            <app-icon-v2 name="code" [size]="15" class="text-violet-600 dark:text-violet-400" />
          </div>
          <h3 class="text-sm font-semibold tracking-tight text-gray-900 dark:text-white uppercase">HTML Snippet</h3>
        </div>

        <div class="flex flex-wrap items-end gap-6 mb-6">
          <!-- Source Input -->
          <div class="flex flex-col gap-2 w-full sm:w-64">
            <div class="flex items-center gap-1.5">
              <label for="snippet-source"
                class="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
                Source
              </label>
              <div class="relative flex items-center group/source-info cursor-help">
                <app-icon-v2 name="info" [size]="13" class="text-gray-400 dark:text-gray-500 hover:text-violet-500 transition-colors" />
                <div
                  class="absolute bottom-full left-0 z-50 mb-2 w-64 p-3 bg-gray-900 dark:bg-gray-950 rounded-xl border border-gray-700/80 text-left text-[11px] text-gray-200 shadow-xl opacity-0 invisible group-hover/source-info:opacity-100 group-hover/source-info:visible transition-all duration-200">
                  <p class="font-semibold text-white mb-1.5">Source is Required</p>
                  <p class="leading-relaxed mb-2 opacity-90">
                    Defines where the chatbot is currently integrated. You can use different sources (e.g., "docs", "portal", "marketing-site") to track engagement. 
                  </p>
                  <div class="p-2 bg-gray-800/50 rounded-lg border border-gray-700/30 text-violet-300 font-mono text-[10px]">
                    source="website-main"
                  </div>
                </div>
              </div>
            </div>
            <input 
              id="snippet-source"
              type="text" 
              [ngModel]="source()" 
              (ngModelChange)="source.set($event)"
              placeholder="e.g. website-main"
              class="w-full bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-xl px-4 py-2 text-xs font-medium focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500/40 outline-none transition-all placeholder:text-gray-400 dark:text-white"
            />
          </div>

          <!-- User ID Info -->
          <div class="flex flex-col gap-2 pb-2">
            <label class="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
              User Identification
            </label>
            <div class="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 font-medium">
              <div class="flex h-5 w-5 items-center justify-center rounded-full bg-violet-500/10 text-violet-500">
                <app-icon-v2 name="sparkles" [size]="10" />
              </div>
              <span><code class="text-violet-500 dark:text-violet-400 font-bold">userId</code> is passed dynamically from the parent website</span>
            </div>
          </div>
        </div>

      </div>
      <div
        class="w-full relative group/snippet overflow-hidden rounded-xl border border-gray-200/80 dark:border-gray-800/60 bg-gray-50/30 dark:bg-gray-950/20">
        <div
          class="flex items-center justify-between px-4 py-2.5 bg-gray-100/50 dark:bg-gray-900/40 border-b border-gray-200/60 dark:border-gray-800/40">
          <div class="flex gap-1.5">
            <div class="w-2.5 h-2.5 rounded-full bg-gray-300/50 dark:bg-gray-700/50"></div>
            <div class="w-2.5 h-2.5 rounded-full bg-gray-300/30 dark:bg-gray-700/30"></div>
            <div class="w-2.5 h-2.5 rounded-full bg-gray-300/20 dark:bg-gray-700/20"></div>
          </div>
          <span
            class="text-[10px] font-mono font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest px-2 py-0.5 rounded-md bg-gray-200/40 dark:bg-gray-800/30">ready</span>
        </div>
        <div class="p-5 overflow-x-auto scrollbar-theme">
          <pre
            class="text-[13px] font-mono text-gray-700 dark:text-gray-300 leading-relaxed break-words whitespace-pre-wrap"><code [innerText]="generatedSnippet()"></code></pre>
        </div>
        <div
          class="px-5 py-4 bg-gray-100/30 dark:bg-gray-900/20 border-t border-gray-200/40 dark:border-gray-800/20 flex justify-end">
          <app-button variant="default" [iconNameV2]="'copy'"
            className="flex items-center justify-center gap-2 text-xs font-semibold py-2 px-6"
            (buttonClick)="copySnippet()">
            Copy Code Snippet
          </app-button>
        </div>
      </div>
    </div>
  </div>


  <!-- Appearance & Behavior Accordion -->
  <form #configForm="ngForm"
    class="relative overflow-visible backdrop-blur-sm bg-white dark:bg-gray-900 rounded-2xl border border-gray-200/90 dark:border-gray-700/70 bg-gradient-to-br from-white via-gray-50/80 to-violet-50/30 dark:from-gray-900/80 dark:via-gray-900/50 dark:to-violet-950/20 shadow-sm shadow-gray-900/5 dark:shadow-black/20 p-6 sm:p-8">
    <div
      class="pointer-events-none absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-violet-500 to-indigo-600 rounded-l-2xl">
    </div>
    <div class="space-y-4">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3 mb-2">
          <div
            class="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500/10 to-indigo-600/5 ring-1 ring-violet-500/15 dark:from-violet-400/10 dark:to-indigo-500/5 dark:ring-violet-400/20 shadow-sm">
            <app-icon-v2 name="sparkles" [size]="18" class="text-violet-600 dark:text-violet-400" />
          </div>
          <div>
            <h3 class="text-[15px] font-bold text-gray-900 dark:text-white tracking-tight uppercase">Appearance &
              Behavior
            </h3>
            <p class="text-[12px] text-gray-500 dark:text-gray-400">Configure how the chatbot widget looks and feels on
              your site.</p>
          </div>
        </div>
        <button type="button" (click)="saveConfig()" [disabled]="isSaving() || configForm.invalid"
          class="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold bg-violet-600 hover:bg-violet-700 text-white shadow-md shadow-violet-500/20 transition-all disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer">
          @if (isSaving()) {
          <span class="inline-block w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin"></span>
          Saving...
          } @else {
          <app-icon-v2 name="save" [size]="16" />
          Save Changes
          }
        </button>
      </div>

      <div class="space-y-3">
        @for (section of sections; track section.id) {
        <div
          class="group/section rounded-xl border border-gray-200/60 dark:border-gray-700/40 bg-white/40 dark:bg-gray-800/20 transition-all duration-300 shadow-sm"
          [ngClass]="{
             'ring-1 ring-violet-500/20 overflow-visible': isSectionOpen(section.id),
             'overflow-hidden': !isSectionOpen(section.id)
           }">
          <button (click)="toggleSection(section.id)"
            class="flex w-full items-center justify-between px-5 py-4 text-left hover:bg-gray-50/50 dark:hover:bg-gray-700/20 transition-colors">
            <div class="flex items-center gap-3">
              <app-icon-v2 [name]="section.icon" [size]="16"
                class="text-gray-400 group-hover/section:text-violet-500 dark:text-gray-500 dark:group-hover/section:text-violet-400 transition-colors" />
              <span class="text-sm font-semibold text-gray-700 dark:text-gray-200">{{ section.label }}</span>
            </div>
            <app-icon-v2 [name]="isSectionOpen(section.id) ? 'chevronUp' : 'chevronDown'" [size]="16"
              class="text-gray-400 transition-transform duration-300" />
          </button>

          <div *ngIf="isSectionOpen(section.id)"
            class="px-5 pb-6 pt-2 border-t border-gray-200/40 dark:border-gray-700/20 bg-gray-50/10 dark:bg-gray-900/10">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
              @for (field of section.fields; track field.path) {
              <div class="space-y-1.5" [class.col-span-full]="field.type === 'textarea'">
                <div class="flex justify-between items-center">
                  <label class="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {{ field.label }}
                  </label>
                </div>

                @switch (field.type) {
                @case ('text') {
                <div class="relative flex items-center">
                  <input type="text" [ngModel]="getValue(field.path)" (ngModelChange)="updateConfig(field.path, $event)"
                    [placeholder]="field.placeholder || ''"
                    [attr.maxlength]="field.maxLength || null"
                    class="w-full text-xs bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-2 pr-12 rounded-lg focus:outline-none focus:ring-1 focus:ring-violet-500/30" />
                  <span *ngIf="field.maxLength" 
                    class="absolute right-3 text-[10px] text-gray-400 font-mono tabular-nums bg-white/10 dark:bg-gray-900/10 px-1 rounded">
                    {{ (getValue(field.path) || '').length }}/{{ field.maxLength }}
                  </span>
                </div>
                }
                @case ('number') {
                <input type="number" [ngModel]="getValue(field.path)" (ngModelChange)="updateConfig(field.path, $event)"
                  #numModel="ngModel" [name]="field.path"
                  [min]="getDynamicMin(field)" [max]="getDynamicMax(field)"
                  [class.border-red-500]="numModel.invalid && (numModel.dirty || numModel.touched)"
                  [class.focus:ring-red-500]="numModel.invalid && (numModel.dirty || numModel.touched)"
                  class="w-full text-xs bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-2 rounded-lg focus:outline-none focus:ring-1 focus:ring-violet-500/30 transition-colors" />
                
                @if (numModel.invalid && (numModel.dirty || numModel.touched)) {
                  @if (numModel.errors?.['min']) {
                    <p class="text-[10px] text-red-500 mt-1 font-medium transition-all">Value must be at least {{ getDynamicMin(field) }}</p>
                  }
                  @if (numModel.errors?.['max']) {
                    <p class="text-[10px] text-red-500 mt-1 font-medium transition-all">Value must be at most {{ getDynamicMax(field) }}</p>
                  }
                }
                }
                @case ('color') {
                <div class="relative">
                  <div
                    class="flex items-center gap-2 w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-1.5 rounded-lg hover:border-violet-500/50 transition-all group overflow-visible">
                    <button [colorPicker]="getValue(field.path)" (colorPickerChange)="updateConfig(field.path, $event)"
                      [cpPosition]="'auto'" [cpAlphaChannel]="'always'" [cpOutputFormat]="'hex'"
                      [cpSaveClickOutside]="true" [cpOKButton]="false" [cpDialogDisplay]="'popup'"
                      [cpUseRootViewContainer]="false"
                      class="w-7 h-7 rounded-md shadow-inner border border-black/5 dark:border-white/10 shrink-0 transition-transform active:scale-95 flex-none"
                      [style.backgroundColor]="getValue(field.path)">
                    </button>
                    <input type="text" [ngModel]="getValue(field.path) | uppercase"
                      (ngModelChange)="updateConfig(field.path, $event)"
                      class="flex-1 min-w-0 bg-transparent border-none text-[11px] font-mono text-gray-700 dark:text-gray-300 uppercase tracking-tight focus:outline-none focus:ring-0 p-1"
                      placeholder="#HEX" />
                  </div>

                </div>
                }
                @case ('toggle') {
                <div class="pt-1">
                  <app-toggle [checked]="getValue(field.path)" (toggleChange)="updateConfig(field.path, $event)" />
                </div>
                }
                @case ('textarea') {
                <textarea rows="2" [ngModel]="getValue(field.path)" (ngModelChange)="updateConfig(field.path, $event)"
                  [placeholder]="field.placeholder || ''"
                  class="w-full text-xs bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-2 rounded-lg focus:outline-none focus:ring-1 focus:ring-violet-500/30 transition-all"></textarea>
                }
                }
              </div>
              }
            </div>

            <!-- Custom blocks for section-specific complex logic -->
            @if (section.id === 'behavior') {
            <div class="mt-8 border-t border-gray-200/40 dark:border-gray-700/20 pt-6">
              <div class="flex items-center justify-between mb-3">
                <label
                  class="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Suggestion
                  Prompts</label>
                <button (click)="addSuggestionPrompt()"
                  class="text-[10px] font-bold text-violet-600 dark:text-violet-400 uppercase flex items-center gap-1 hover:text-violet-500 transition-colors">
                  <app-icon-v2 name="plus" [size]="12" />
                  Add Prompt
                </button>
              </div>
              <div class="space-y-2">
                @for (p of embedConfig().behavior.suggestionPrompts; track $index; let i = $index) {
                <div class="flex items-center gap-2">
                  <input type="text" [ngModel]="p" (ngModelChange)="updateSuggestionPrompt(i, $event)"
                    class="flex-1 text-xs bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-2 rounded-lg focus:outline-none focus:ring-1 focus:ring-violet-500/30" />
                  <button (click)="removeSuggestionPrompt(i)"
                    class="p-2 text-gray-400 hover:text-red-500 transition-colors">
                    <app-icon-v2 name="trash" [size]="14" />
                  </button>
                </div>
                }
                @if (embedConfig().behavior.suggestionPrompts.length === 0) {
                <div class="py-6 text-center border border-dashed border-gray-200 dark:border-gray-800 rounded-xl">
                  <p class="text-[11px] text-gray-400 font-medium">No suggestion prompts added yet.</p>
                </div>
                }
              </div>
            </div>
            }
          </div>
        </div>
        }
      </div>
    </div>
  </form>
</div>
